/* OMO stockist page controller (presentation only, all stockists are server-rendered).
   One panel is shown at a time; prev / next step through the blocks in section order, the URL hash
   names the active stockist (#pulp), Back / Forward and Theme Editor block selection stay in sync. */
(() => {
  const controller = window.OMOStockistPage;
  if (controller) {
    controller.initAll();
    return;
  }

  const rootSelector = '[data-omo-stockist-page]';
  const panelSelector = '[data-omo-stockist-panel]';

  const getHash = () => {
    const hash = window.location.hash.slice(1);

    try {
      return decodeURIComponent(hash);
    } catch (_error) {
      return hash;
    }
  };

  const getPanels = (root) => [...root.querySelectorAll(panelSelector)];

  const setArrow = (root, selector, disabled) => {
    root.querySelectorAll(selector).forEach((button) => {
      button.disabled = disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });
  };

  const activate = (root, requestedKey, updateHistory = false) => {
    const panels = getPanels(root);
    if (panels.length === 0) return;

    const activePanel = panels.find((panel) => panel.dataset.omoStockistPanel === requestedKey) || panels[0];
    panels.forEach((panel) => {
      panel.hidden = panel !== activePanel;
    });

    const index = panels.indexOf(activePanel);
    const name = activePanel.querySelector('[data-omo-stockist-name]')?.textContent.trim() || '';
    root.querySelectorAll('[data-omo-stockist-label]').forEach((label) => {
      label.textContent = name;
    });
    setArrow(root, '[data-omo-stockist-prev]', index === 0);
    setArrow(root, '[data-omo-stockist-next]', index === panels.length - 1);

    const key = activePanel.dataset.omoStockistPanel;
    root.querySelectorAll('[data-omo-stockist-trigger]').forEach((button) => {
      const isActive = button.dataset.omoStockistTrigger === key;
      button.classList.toggle('omo-stockist-page__index-button--active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (updateHistory && getHash() !== key) {
      const url = new URL(window.location.href);
      url.hash = key;
      window.history.pushState({ omoStockist: key }, '', url);
    }
  };

  const initRoot = (root) => {
    if (root.dataset.omoStockistInitialized === 'true') return;
    if (getPanels(root).length === 0) return;

    root.dataset.omoStockistInitialized = 'true';
    root.classList.add('omo-stockist-page--js');
    root.omoStockistActivate = (key, updateHistory = false) => activate(root, key, updateHistory);

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-omo-stockist-trigger]');
      if (trigger && root.contains(trigger)) {
        activate(root, trigger.dataset.omoStockistTrigger, true);
        return;
      }

      const arrow = event.target.closest('[data-omo-stockist-prev], [data-omo-stockist-next]');
      if (!arrow || !root.contains(arrow)) return;

      const panels = getPanels(root);
      const current = panels.findIndex((panel) => !panel.hidden);
      const target = panels[current + (arrow.hasAttribute('data-omo-stockist-next') ? 1 : -1)];
      if (target) activate(root, target.dataset.omoStockistPanel, true);
    });

    activate(root, getHash());
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(rootSelector)) initRoot(scope);
    scope.querySelectorAll?.(rootSelector).forEach(initRoot);
  };

  const syncFromHash = () => {
    document.querySelectorAll(rootSelector).forEach((root) => {
      initRoot(root);
      root.omoStockistActivate?.(getHash());
    });
  };

  window.OMOStockistPage = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  window.addEventListener('popstate', syncFromHash);
  window.addEventListener('hashchange', syncFromHash);

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;

    const panel = [...document.querySelectorAll(`${rootSelector} ${panelSelector}`)].find(
      (candidate) => candidate.dataset.omoStockistBlockId === blockId,
    );
    if (!panel) return;

    const root = panel.closest(rootSelector);
    initRoot(root);
    root.omoStockistActivate?.(panel.dataset.omoStockistPanel);
  });
})();
